import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { format } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import Loading from "components/Loading";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import {
  approvePromptCandidate,
  rejectPromptCandidate,
  returnCandidateToFdec,
} from "services/fde/annotationService";
import {
  loadCandidateForReview,
  loadManagedDomains,
} from "services/fde/tciManagerService";
import { HELP_VIDEO_WORKFLOWS } from "services/helpVideoService";
import { useRegisterHelpWorkflow } from "hooks/useRegisterHelpWorkflow";
import { getHttpsCallableErrorMessage } from "services/partnerTierService";
import { diffPromptLines } from "utils/promptDiff";

function formatPromptLabel(promptId) {
  return (
    String(promptId || "")
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase()) || "Prompt"
  );
}

const CandidateReviewPage = () => {
  useRegisterHelpWorkflow(HELP_VIDEO_WORKFLOWS.TCI_MANAGER_CANDIDATE_REVIEW);
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { user, isGlobalAdmin, userData, organizationId } = useAuth();
  const { isFdeDc, loading: fdeLoading } = useFdeRole();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const canAccess = isFdeDc || isGlobalAdmin;

  const rejectForm = useForm({ defaultValues: { reviewNotes: "" } });
  const returnForm = useForm({ defaultValues: { questions: "" } });

  const loadReview = useCallback(async () => {
    if (!candidateId || !institutionId || !user?.uid || !canAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const domains = await loadManagedDomains({ uid: user.uid, institutionId });
      const managedTciIds = [
        ...new Set(domains.flatMap((domain) => domain.tciIds || []).filter(Boolean)),
      ];
      const data = await loadCandidateForReview({
        candidateId,
        institutionId,
        managedTciIds: isGlobalAdmin ? [] : managedTciIds,
      });
      if (!data) {
        setErrorMessage("Candidate not found or outside your TCI scope.");
        setReviewData(null);
      } else {
        setReviewData(data);
      }
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load candidate.");
    } finally {
      setLoading(false);
    }
  }, [candidateId, institutionId, user?.uid, canAccess, isGlobalAdmin]);

  useEffect(() => {
    if (!fdeLoading) loadReview();
  }, [loadReview, fdeLoading]);

  const diffLines = useMemo(() => {
    if (!reviewData?.candidate) return [];
    return diffPromptLines(
      reviewData.candidate.currentPromptSnapshot,
      reviewData.candidate.proposedPromptText,
    );
  }, [reviewData]);

  const handlePromote = async () => {
    setActionLoading(true);
    setErrorMessage("");
    try {
      await approvePromptCandidate({ candidateId });
      setSuccessMessage("Candidate promoted.");
      setPromoteOpen(false);
      navigate("/dashboard/tci-manager");
    } catch (err) {
      setPromoteOpen(false);
      setErrorMessage(
        getHttpsCallableErrorMessage(err, "Promotion failed."),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = rejectForm.handleSubmit(async (values) => {
    setActionLoading(true);
    setErrorMessage("");
    try {
      await rejectPromptCandidate({
        candidateId,
        reviewNotes: values.reviewNotes,
      });
      setSuccessMessage("Candidate rejected.");
      setRejectOpen(false);
      navigate("/dashboard/tci-manager");
    } catch (err) {
      setErrorMessage(err?.message || "Rejection failed.");
    } finally {
      setActionLoading(false);
    }
  });

  const handleReturn = returnForm.handleSubmit(async (values) => {
    setActionLoading(true);
    setErrorMessage("");
    try {
      await returnCandidateToFdec({
        candidateId,
        questions: values.questions,
      });
      setSuccessMessage("Returned to your team for additional input.");
      setReturnOpen(false);
      navigate("/dashboard/tci-manager");
    } catch (err) {
      setErrorMessage(err?.message || "Return failed.");
    } finally {
      setActionLoading(false);
    }
  });

  if (fdeLoading || loading) return <Loading page />;

  if (!canAccess) {
    return <Alert severity="warning">TCI Manager access required.</Alert>;
  }

  if (!reviewData?.candidate) {
    return (
      <Box>
        <DashboardPageHeader title="Candidate review" />
        <Alert severity="error">{errorMessage || "Candidate unavailable."}</Alert>
      </Box>
    );
  }

  const { candidate, annotations } = reviewData;

  return (
    <Box>
      <DashboardPageHeader
        title="Review candidate"
        subtitle={`${formatPromptLabel(candidate.promptId)} · ${formatPromptLabel(candidate.tciId) || "TCI"}`}
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      {Array.isArray(candidate.testCaseWarnings) && candidate.testCaseWarnings.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Prompt test warnings: {candidate.testCaseWarnings.join(" · ")}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: "100%" }} data-testid="candidate-annotation-panel">
            <Typography variant="subtitle1" gutterBottom>
              What your clinicians observed
            </Typography>
            <Stack spacing={2}>
              {annotations.map((row) => (
                <Box key={row.id}>
                  <Typography variant="subtitle2">{row.teacherName}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    data-testid="annotation-severity-label"
                  >
                    {row.sessionDate ? format(row.sessionDate, "MMM d, yyyy") : "Session date unknown"}
                    {" · "}
                    {row.sessionAnnotation?.severity || "medium"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {row.sessionAnnotation?.notes || "No session notes."}
                  </Typography>
                  {row.sessionId && (
                    <Typography
                      component={RouterLink}
                      to={`/dashboard/sessions/${row.sessionId}`}
                      variant="body2"
                      color="primary"
                      sx={{ display: "inline-block", mt: 0.5 }}
                    >
                      View full session
                    </Typography>
                  )}
                </Box>
              ))}
              {!annotations.length && (
                <Typography variant="body2" color="text.secondary">
                  No contributing annotations linked to this candidate.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" gutterBottom>
              Current active prompt
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Snapshot at candidate generation — may differ from live prompt today.
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "action.hover",
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                fontSize: "0.8rem",
                maxHeight: 480,
                overflow: "auto",
              }}
            >
              {candidate.currentPromptSnapshot || "(empty snapshot)"}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" gutterBottom>
              Proposed improvement
            </Typography>
            {candidate.diffSummary && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {candidate.diffSummary}
              </Typography>
            )}
            {Array.isArray(candidate.checksDriving) && candidate.checksDriving.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                {candidate.checksDriving.map((check) => (
                  <Chip key={check} size="small" label={check} />
                ))}
              </Stack>
            )}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "action.hover",
                maxHeight: 480,
                overflow: "auto",
                fontFamily: "monospace",
                fontSize: "0.8rem",
              }}
            >
              {diffLines.map((line) => (
                <Box
                  key={line.index}
                  sx={{
                    whiteSpace: "pre-wrap",
                    bgcolor: line.changed ? "warning.light" : "transparent",
                    px: line.changed ? 0.5 : 0,
                    borderRadius: 0.5,
                  }}
                >
                  {line.text}
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 3 }}>
        <LoadingButton variant="contained" onClick={() => setPromoteOpen(true)}>
          Promote
        </LoadingButton>
        <LoadingButton variant="outlined" color="error" onClick={() => setRejectOpen(true)}>
          Reject
        </LoadingButton>
        <LoadingButton variant="outlined" onClick={() => setReturnOpen(true)}>
          Return to team
        </LoadingButton>
        <LoadingButton onClick={() => navigate("/dashboard/tci-manager")}>
          Back
        </LoadingButton>
      </Stack>

      <ConfirmDialog
        open={promoteOpen}
        title="Promote this candidate?"
        message="This will replace the active organization prompt with the proposed text."
        confirmLabel="Promote"
        confirmColor="primary"
        loading={actionLoading}
        onConfirm={handlePromote}
        onClose={() => setPromoteOpen(false)}
      />

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject candidate</DialogTitle>
        <Box component="form" onSubmit={handleReject}>
          <DialogContent>
            <Controller
              name="reviewNotes"
              control={rejectForm.control}
              rules={{ required: "Please provide a rejection reason." }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={3}
                  label="Rejection reason"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <LoadingButton onClick={() => setRejectOpen(false)}>Cancel</LoadingButton>
            <LoadingButton type="submit" color="error" loading={actionLoading}>
              Reject
            </LoadingButton>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Return to your team</DialogTitle>
        <Box component="form" onSubmit={handleReturn}>
          <DialogContent>
            <Controller
              name="questions"
              control={returnForm.control}
              rules={{ required: "Please describe what additional input you need." }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={4}
                  label="Questions for your Clinical Annotators"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <LoadingButton onClick={() => setReturnOpen(false)}>Cancel</LoadingButton>
            <LoadingButton type="submit" loading={actionLoading}>
              Return
            </LoadingButton>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default CandidateReviewPage;
